import TextField, { type TextFieldProps } from '@mui/material/TextField';
import { type FC, useEffect, useState } from 'react';

type ClampedNumberFieldProps = Omit<TextFieldProps, 'value' | 'onChange' | 'type'> & {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
};

/**
 * Number input that allows free typing while the field is focused, and clamps the value
 * to the [min, max] range when the field loses focus. The parent always receives the raw
 * typed value via `onChange`, so submit-time validation in the parent can still inspect
 * out-of-range values before the user blurs.
 */
const ClampedNumberField: FC<ClampedNumberFieldProps> = ({ value, onChange, min, max, error, helperText, ...rest }) => {
  const [draft, setDraft] = useState<string>(String(value));

  // Keep local draft in sync when the parent value changes from the outside (e.g. blur clamp).
  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const parsed = draft === '' ? Number.NaN : Number(draft);
  const isInvalid =
    draft === '' || Number.isNaN(parsed) || (min !== undefined && parsed < min) || (max !== undefined && parsed > max);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setDraft(next);
    const n = Number(next);
    if (next !== '' && !Number.isNaN(n)) {
      onChange(n);
    }
  };

  const handleBlur = () => {
    let n = Number(draft);
    if (draft === '' || Number.isNaN(n)) {
      n = min ?? 0;
    }
    if (min !== undefined) n = Math.max(min, n);
    if (max !== undefined) n = Math.min(max, n);
    setDraft(String(n));
    if (n !== value) onChange(n);
  };

  const rangeMessage = (() => {
    if (!isInvalid) return null;
    if (min !== undefined && max !== undefined) return `Debe estar entre ${min} y ${max}`;
    if (min !== undefined) return `Debe ser mayor o igual a ${min}`;
    if (max !== undefined) return `Debe ser menor o igual a ${max}`;
    return 'Valor inválido';
  })();

  return (
    <TextField
      {...rest}
      type="number"
      value={draft}
      onChange={handleChange}
      onBlur={handleBlur}
      error={error || isInvalid}
      helperText={rangeMessage ?? helperText}
    />
  );
};

export default ClampedNumberField;
